import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  startAfter,
  updateDoc,
  writeBatch,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { streamApi } from '../../../services/apiClient';
import type { CompiledQuest, QuestInsightsData, QuestJourney, QuestTemplateFirestore } from './questTypes';

/** Controller for stopping a chat quest test */
export interface ChatQuestTestController {
  stop: () => Promise<void>;
}

/** Callback type for streaming chat quest test */
export type ChatQuestStreamCallback = (item: DisplayItem) => void;

/** What `adminTestChatQuest` takes. Mirrors the route's request interface. */
interface ChatQuestTestRequest {
  journeyDayId: string;
  questTemplateId: string;
  extraGuidance: string;
  maxTurns: number;
  questType: 'chat' | 'check-in';
}

/**
 * A chunk off the stream. `keepalive` is the server's idle-connection ping — the callable
 * SSE protocol only carries `data:` frames, so it arrives as a chunk to skip rather than a
 * comment the reader would ignore. It is filtered here and never reaches the UI.
 */
type ChatQuestTestChunk = DisplayItem | { type: 'keepalive' };

interface ChatQuestTestResult {
  /** Messages exchanged, which is what the modal renders. */
  totalTurns: number;
  stoppedReason: 'max_turns' | 'end_conversation' | 'no_text' | 'deadline';
}

/** Above the handler's own run budget, so the server is the one that decides to stop. */
const CLIENT_TIMEOUT_MS = 600_000;

const collectionName = 'quest_journey_templates';

/** Structured message for display */
export interface DisplayMessage {
  type: 'message';
  role: 'quest_ai' | 'tester';
  content: string;
  durationMs: number;
  turnIndex?: number;
}

/** Structured tool call for display */
export interface DisplayToolCall {
  type: 'tool_call';
  name: string;
  arguments: string;
  durationMs: number;
  turnIndex?: number;
}

export type DisplayItem = DisplayMessage | DisplayToolCall;

export class QuestsService {
  private static instance: QuestsService;

  private constructor() { }

  public static getInstance(): QuestsService {
    if (!QuestsService.instance) {
      QuestsService.instance = new QuestsService();
    }
    return QuestsService.instance;
  }

  public async getQuestJourneyDays(): Promise<string[]> {
    const questJourneyDays = await getDocs(collection(db, collectionName));
    return questJourneyDays.docs
      .map((doc) => doc.id)
      .sort((a, b) => {
        const numA = parseInt(a.replace('day_', ''), 10);
        const numB = parseInt(b.replace('day_', ''), 10);
        return numA - numB;
      });
  }

  public async getCurrentUserJourney(userId: string): Promise<QuestJourney> {
    const userRef = doc(db, 'user_info', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists) {
      throw new Error('User not found');
    }
    return userDoc.data()!.questJourney as QuestJourney;
  }

  public async getQuestTemplates(journeyDayId: string): Promise<QuestTemplateFirestore[]> {
    const questTemplates = await getDocs(collection(db, collectionName, journeyDayId, 'quest_templates'));
    // add id to each quest template
    return questTemplates.docs.map((doc) => {
      return { ...doc.data(), id: doc.id } as QuestTemplateFirestore;
    });
  }

  public async getAllQuestTemplates(): Promise<QuestTemplateFirestore[]> {
    // Query all 'quest_templates' collections across all journey days using collectionGroup
    // This will find: /quest_journey_templates/{dayId}/quest_templates/{templateId}
    const questTemplatesCollectionGroup = collectionGroup(db, 'quest_templates');
    const snapshot = await getDocs(questTemplatesCollectionGroup);

    const questTemplates: QuestTemplateFirestore[] = snapshot.docs.map((doc) => {
      // Extract journey day ID from document path
      // Path format: quest_journey_templates/{dayId}/quest_templates/{templateId}
      const pathSegments = doc.ref.path.split('/');
      const journeyDayId = pathSegments[1]; // The dayId is at index 1

      return {
        ...doc.data(),
        id: doc.id,
        journeyDayId,
      } as QuestTemplateFirestore;
    });

    return questTemplates;
  }

  public async createQuestTemplate(journeyDayId: string, questTemplate: QuestTemplateFirestore): Promise<QuestTemplateFirestore> {
    const questTemplateRef = collection(db, collectionName, journeyDayId, 'quest_templates');
    const questTemplateToCreate = { ...questTemplate };
    delete questTemplateToCreate.id;
    const docRef = await addDoc(questTemplateRef, questTemplateToCreate);
    return { ...questTemplateToCreate, id: docRef.id } as QuestTemplateFirestore;
  }

  public async updateQuestTemplate(journeyDayId: string, questTemplate: QuestTemplateFirestore): Promise<void> {
    console.log('questTemplate', questTemplate);
    const questTemplateRef = doc(db, collectionName, journeyDayId, 'quest_templates', questTemplate.id!);
    //ommit id from the update
    delete questTemplate.id;
    await updateDoc(questTemplateRef, { ...questTemplate });
  }

  public async deleteQuestTemplate(journeyDayId: string, questTemplate: QuestTemplateFirestore): Promise<void> {
    const questTemplateRef = doc(db, collectionName, journeyDayId, 'quest_templates', questTemplate.id!);
    await deleteDoc(questTemplateRef);
  }

  public async createJourneyDay(journeyDayNumber: number): Promise<void> {
    const journeyDayRef = doc(db, collectionName, `day_${journeyDayNumber}`);
    await setDoc(journeyDayRef, {});
  }

  public async updateQuestOrders(journeyDayId: string, questTemplates: QuestTemplateFirestore[]): Promise<void> {
    // Update each quest template with the new order
    const updatePromises = questTemplates.map(async (questTemplate) => {
      const questTemplateRef = doc(db, collectionName, journeyDayId, 'quest_templates', questTemplate.id!);
      await updateDoc(questTemplateRef, { order: questTemplate.order });
    });

    await Promise.all(updatePromises);
  }

  public async publishAllQuests(journeyDayId: string, questTemplates: QuestTemplateFirestore[]): Promise<void> {
    // Use batch write to publish all quests in a single transaction
    const batch = writeBatch(db);

    questTemplates.forEach((questTemplate) => {
      const questTemplateRef = doc(db, collectionName, journeyDayId, 'quest_templates', questTemplate.id!);
      batch.update(questTemplateRef, { isPublished: true });
    });

    await batch.commit();
  }

  /**
   * Test chat quest with streaming - messages are delivered as they arrive via callback.
   * Returns a controller that can be used to stop the test.
   */
  public async testChatQuestStream(
    journeyDayId: string,
    questTemplateId: string,
    extraGuidance: string,
    maxTurns: number,
    questType: 'chat' | 'check-in',
    onMessage: ChatQuestStreamCallback,
    onComplete: (totalTurns: number) => void,
    onError: (error: string) => void
  ): Promise<ChatQuestTestController> {
    // Aborting the fetch *is* the stop. The API's streaming wrapper turns a client hang-up
    // into an AbortSignal the handler's model calls honour, which is why there is no longer
    // a session id to track or a stop endpoint to POST to.
    const abortController = new AbortController();
    let timedOut = false;

    const clientTimeout = setTimeout(() => {
      timedOut = true;
      abortController.abort();
    }, CLIENT_TIMEOUT_MS);

    let stream: AsyncIterable<ChatQuestTestChunk>;
    let data: Promise<ChatQuestTestResult>;
    try {
      ({ stream, data } = await streamApi<ChatQuestTestRequest, ChatQuestTestChunk, ChatQuestTestResult>(
        '/adminTestChatQuest',
        { journeyDayId, questTemplateId, extraGuidance, maxTurns, questType },
        { signal: abortController.signal }
      ));
    } catch (error) {
      // Rejected before a byte was streamed: not signed in, not an admin, bad input.
      // Reported rather than thrown, because the caller does not catch.
      clearTimeout(clientTimeout);
      onError(error instanceof Error ? error.message : 'Failed to start test');
      return { stop: async () => { } };
    }

    void (async () => {
      try {
        for await (const chunk of stream) {
          if (chunk.type === 'keepalive') continue;
          onMessage(chunk);
        }
        const { totalTurns } = await data;
        onComplete(totalTurns);
      } catch (error) {
        if (timedOut) {
          onError('Request timed out after 10 minutes');
        } else if (!abortController.signal.aborted) {
          // A deliberate stop is not an error -- the component has already moved to
          // 'stopped' and saved the partial conversation.
          onError(error instanceof Error ? error.message : 'Unknown error occurred');
        }
      } finally {
        clearTimeout(clientTimeout);
      }
    })();

    return {
      stop: async () => {
        clearTimeout(clientTimeout);
        abortController.abort();
      },
    };
  }

  public async updateUserJourney(userId: string, currentDay: number, isCurrentDayCompleted: boolean): Promise<void> {
    const userRef = doc(db, 'user_info', userId);
    const date = new Date();
    if (isCurrentDayCompleted) {
      date.setDate(date.getDate() - 1);
    }
    await updateDoc(userRef, {
      'questJourney.currentDay': currentDay,
      'questJourney.isCurrentDayCompleted': isCurrentDayCompleted,
      'questJourney.journeyDayCompletedAt': date,
      'questJourney.journeyDayStartedAt': date,
    });
  }

  public async jumpToDay(userId: string, targetDay: number): Promise<void> {
    const userRef = doc(db, 'user_info', userId);
    await updateDoc(userRef, {
      'questJourney.currentDay': targetDay,
      'questJourney.isCurrentDayCompleted': false,
      'questJourney.journeyDayStartedAt': new Date(),
    });
  }

  public async deleteUserJourney(userId: string): Promise<void> {
    const userRef = doc(db, 'user_info', userId);
    await updateDoc(userRef, {
      questJourney: null,
    });
  }

  public async resetFieldCollectedValues(userId: string, keys: string[]) {
    // batch delete all documents with id in the keys array
    const batch = writeBatch(db);
    keys.forEach((key) => {
      const docRef = doc(db, 'user_info', userId, 'ocd_profile', key);
      batch.delete(docRef);
    });
    await batch.commit();
  }

  public async getAllCompiledQuests(
    pageSize: number = 50,
    lastDoc?: QueryDocumentSnapshot
  ): Promise<{ quests: CompiledQuest[]; lastDoc: QueryDocumentSnapshot | null }> {
    // Query all 'quests' collections across all users using collectionGroup
    // This will find: /user_info/{userId}/quest_journey/{dayId}/quests/{questId}
    const questsCollectionGroup = collectionGroup(db, 'quests');

    let q = query(questsCollectionGroup, limit(pageSize));

    if (lastDoc) {
      q = query(questsCollectionGroup, startAfter(lastDoc), limit(pageSize));
    }

    const snapshot = await getDocs(q);

    const quests: CompiledQuest[] = snapshot.docs.map(
      (doc) =>
      ({
        ...doc.data(),
        id: doc.id,
        userId: doc.ref.path.split('/')[1],
      } as CompiledQuest)
    );

    const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;

    return { quests, lastDoc: lastVisible };
  }

  public async getQuestInsights(userId: string, weekNumber: number): Promise<QuestInsightsData | null> {
    const insightsRef = doc(db, 'user_info', userId, 'quest_insights', `week_${weekNumber}`);
    const insightsDoc = await getDoc(insightsRef);

    if (!insightsDoc.exists()) {
      return null;
    }

    return insightsDoc.data() as QuestInsightsData;
  }

  public async moveQuestTemplate(
    sourceJourneyDayId: string,
    targetJourneyDayId: string,
    questTemplate: QuestTemplateFirestore
  ): Promise<void> {
    // Get current quests in target day to determine the next order value
    const targetQuests = await this.getQuestTemplates(targetJourneyDayId);
    const maxOrder = targetQuests.length > 0 ? Math.max(...targetQuests.map(q => q.order)) : -1;
    const targetOrder = maxOrder + 1;

    // Create a copy of the quest in the target journey day with isPublished: false
    const questTemplateToMove = { ...questTemplate };
    delete questTemplateToMove.id; // Remove the ID so a new one is generated
    questTemplateToMove.order = targetOrder;
    questTemplateToMove.isPublished = false;

    // Create the quest in the target day
    await this.createQuestTemplate(targetJourneyDayId, questTemplateToMove);

    // Delete the quest from the source day
    await this.deleteQuestTemplate(sourceJourneyDayId, questTemplate);

    // Get remaining quests in source day and reorder them
    const sourceQuests = await this.getQuestTemplates(sourceJourneyDayId);
    sourceQuests.sort((a, b) => a.order - b.order);
    sourceQuests.forEach((quest, index) => {
      quest.order = index;
    });

    // Update orders in source day
    if (sourceQuests.length > 0) {
      await this.updateQuestOrders(sourceJourneyDayId, sourceQuests);
    }
  }
}

export const questsService = QuestsService.getInstance();
